import { createClient } from '@/lib/supabase/client';
import type { WellnessInput, ReadinessStatus } from '@/lib/wellness';
import { calculateReadinessScore, getReadinessStatus, getTrainingAdjustment } from '@/lib/wellness';

// NOTE: Do NOT call createClient() at module top-level — it runs during SSR
// and can crash before env vars are available. Call it inside each function.

export interface WellnessCheckinRow {
  id: string;
  athlete_id: string;
  user_id: string | null;
  checkin_date: string;
  sleep_quality: number;
  sleep_duration: number;
  soreness_level: number;
  stress_level: number;
  energy_level: number;
  mood: number;
  nutrition_quality: number;
  hydration_quality: number;
  body_pain: boolean;
  pain_location: string | null;
  practice_yesterday: boolean;
  practice_today: boolean;
  notes_to_coach: string | null;
  readiness_score: number;
  readiness_status: ReadinessStatus;
  training_adjustment: string | null;
  created_at: string;
  updated_at: string;
}

export interface WellnessWithAthlete extends WellnessCheckinRow {
  athlete_profiles?: {
    id: string;
    name: string;
    sport: string | null;
    position: string | null;
    avatar_initials: string | null;
  };
}

// ─── Submit a check-in ────────────────────────────────────────────────────────

export async function submitWellnessCheckin(
  athleteId: string,
  userId: string | null,
  input: WellnessInput,
  date?: string
): Promise<{ data: WellnessCheckinRow | null; error: string | null }> {
  const supabase = createClient();

  // Always resolve the authenticated user from the session — never trust a
  // caller-supplied userId that might be null or stale.
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    return { data: null, error: 'User must be logged in to submit a wellness check-in.' };
  }

  const score = calculateReadinessScore(input);
  const status = getReadinessStatus(score);
  const adjustment = getTrainingAdjustment(status, input);
  const checkinDate = date ?? new Date().toISOString().split('T')[0];

  const payload = {
    athlete_id: athleteId,
    user_id: user.id,          // always auth.uid() — required by RLS
    checkin_date: checkinDate,
    sleep_quality: input.sleepQuality,
    sleep_duration: input.sleepDuration,
    soreness_level: input.sorenessLevel,
    stress_level: input.stressLevel,
    energy_level: input.energyLevel,
    mood: input.mood,
    nutrition_quality: input.nutritionQuality,
    hydration_quality: input.hydrationQuality,
    body_pain: input.bodyPain,
    pain_location: input.painLocation ?? null,
    practice_yesterday: input.practiceYesterday,
    practice_today: input.practiceToday,
    notes_to_coach: input.notesToCoach ?? null,
    readiness_score: score,
    readiness_status: status,
    training_adjustment: adjustment,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('daily_wellness_checkins')
    .upsert(payload, { onConflict: 'user_id,checkin_date' })
    .select()
    .single();

  if (error) return { data: null, error: error.message };
  return { data: data as WellnessCheckinRow, error: null };
}

// ─── Get today's check-in for an athlete ─────────────────────────────────────

export async function getTodayCheckin(
  athleteId: string
): Promise<{ data: WellnessCheckinRow | null; error: string | null }> {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('daily_wellness_checkins')
    .select('*')
    .eq('athlete_id', athleteId)
    .eq('checkin_date', today)
    .maybeSingle();

  if (error) return { data: null, error: error.message };
  return { data: data as WellnessCheckinRow | null, error: null };
}

// ─── Get recent check-ins for an athlete ─────────────────────────────────────

export async function getAthleteWellnessHistory(
  athleteId: string,
  days = 30
): Promise<{ data: WellnessCheckinRow[]; error: string | null }> {
  const supabase = createClient();
  const since = new Date();
  since.setDate(since.getDate() - days);
  const sinceStr = since.toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('daily_wellness_checkins')
    .select('*')
    .eq('athlete_id', athleteId)
    .gte('checkin_date', sinceStr)
    .order('checkin_date', { ascending: true });

  if (error) return { data: [], error: error.message };
  return { data: (data ?? []) as WellnessCheckinRow[], error: null };
}

// ─── Get all athletes' today check-ins (coach view) ──────────────────────────

export async function getAllAthletesTodayWellness(): Promise<{
  data: WellnessWithAthlete[];
  error: string | null;
}> {
  const supabase = createClient();
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('daily_wellness_checkins')
    .select(`
      *,
      athlete_profiles (
        id,
        name,
        sport,
        position,
        avatar_initials
      )
    `)
    .eq('checkin_date', today)
    .order('readiness_score', { ascending: true });

  if (error) return { data: [], error: error.message };
  return { data: (data ?? []) as WellnessWithAthlete[], error: null };
}

// ─── Get all athletes' latest check-ins (coach view) ─────────────────────────

export async function getAllAthletesLatestWellness(): Promise<{
  data: WellnessWithAthlete[];
  error: string | null;
}> {
  const supabase = createClient();
  // Get the most recent check-in per athlete
  const { data, error } = await supabase
    .from('daily_wellness_checkins')
    .select(`
      *,
      athlete_profiles (
        id,
        name,
        sport,
        position,
        avatar_initials
      )
    `)
    .order('checkin_date', { ascending: false })
    .limit(100);

  if (error) return { data: [], error: error.message };

  // Deduplicate: keep only the latest per athlete
  const seen = new Set<string>();
  const deduped: WellnessWithAthlete[] = [];
  for (const row of (data ?? []) as WellnessWithAthlete[]) {
    if (!seen.has(row.athlete_id)) {
      seen.add(row.athlete_id);
      deduped.push(row);
    }
  }
  return { data: deduped, error: null };
}

// ─── Calculate check-in streak ───────────────────────────────────────────────

export function calculateStreak(history: WellnessCheckinRow[]): number {
  if (!history.length) return 0;
  const sorted = [...history].sort(
    (a, b) => new Date(b.checkin_date).getTime() - new Date(a.checkin_date).getTime()
  );
  const today = new Date().toISOString().split('T')[0];
  let streak = 0;
  let expected = today;

  for (const row of sorted) {
    if (row.checkin_date === expected) {
      streak++;
      const d = new Date(expected);
      d.setDate(d.getDate() - 1);
      expected = d.toISOString().split('T')[0];
    } else {
      break;
    }
  }
  return streak;
}
